import React from 'react';
import { ReactComponent as PassportLogo } from '../svg/passport_logo.svg';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy - Passport</title>
            </Helmet>
            <div className="legal-page">
                <div className="legal-header">
                    <Link to="/">
                        <PassportLogo width="300" className="mb-4" />
                    </Link>
                </div>
                <div className="legal-content">
                    <h1>Passport Privacy Policy</h1>
                    
                    <p><strong>Last Updated:</strong> August 1, 2025</p>
                    
                    <h2>1. About this Policy</h2>
                    <p>
                        Passport is a service offered by INaudio ("Passport", "we", "us", or "our"). This Privacy Policy ("Policy") explains our collection, use, and disclosure of personal data in relation to the use of this website, Passport services, and any other products or services that link to this Policy (collectively, the "Passport Service").
                    </p>
                    
                    <p>
                        The Passport Service covered by this Policy pertain to the following types of individuals:
                    </p>
                    
                    <ul>
                        <li><strong>Publishers</strong> ("Publishers") who use our Passport Service to create, distribute, and monetize audiobooks.</li>
                        <li><strong>Visitors</strong> ("Visitors") who visit our website to learn more about our Passport Service or for other purposes.</li>
                    </ul>
                    
                    <p>
                        From time to time, we may offer new or additional services. Any such services will also be subject to this Policy, unless stated otherwise.
                    </p>
                    
                    <p>
                        This Policy is not the <a href="/terms-of-use">Passport Terms of Use</a>, which is a separate document. The Audiobook License Agreement and the Passport Terms of Use outline the legal contract between you and Passport for using the Passport Service.
                    </p>
                    
                    <h2>2. Your Personal Data Rights and Controls</h2>
                    
                    <h3>I. Rights</h3>
                    <p>
                        We are committed to protecting your privacy and complying with applicable data protection laws in the regions where we operate. As available, and except as limited under applicable law, the rights afforded to individuals are described in the table below. In addition to the rights detailed below, you may also have specific rights under U.S. state and international laws depending on your location. We will use commercially reasonable efforts to comply with all such requests in accordance with applicable privacy laws.
                    </p>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th>Type of Right</th>
                                    <th>It's your right to…</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Be Informed</strong></td>
                                    <td>Be informed of the categories of personal data we process and how we process it.</td>
                                </tr>
                                <tr>
                                    <td><strong>Access</strong></td>
                                    <td>Request access to the personal data we process about you.</td>
                                </tr>
                                <tr>
                                    <td><strong>Rectification</strong></td>
                                    <td>Request that we amend or update your personal data where it's inaccurate or incomplete.</td>
                                </tr>
                                <tr>
                                    <td><strong>Erasure</strong></td>
                                    <td>Request that we erase certain personal data about you. For example, you can ask us to erase personal data: That we no longer need for the purpose it was collected for; When you make a justified objection (see right to 'Object' below). There are situations where we are unable to delete your data, for example when: It's still necessary to process the data for the purpose we collected it for; Passport's interest in using the data overrides your interest in having it deleted, such as needing it to protect our services from fraud; Passport has a legal obligation to keep the data; or Passport needs the data to establish, exercise, or defend legal claims.</td>
                                </tr>
                                <tr>
                                    <td><strong>Restriction</strong></td>
                                    <td>Request that we stop processing some or all of your personal data. You can do this if: Your personal data is inaccurate; Our processing is unlawful; We do not need your information for a specific purpose; or You object to our processing and we are assessing your objection request (see section 'Object' below). You can request that we stop this processing temporarily or permanently.</td>
                                </tr>
                                <tr>
                                    <td><strong>Object</strong></td>
                                    <td>Object to us processing your personal data. You can do this if Passport is processing your personal data on the legal basis of legitimate interests.</td>
                                </tr>
                                <tr>
                                    <td><strong>Data Portability</strong></td>
                                    <td>Request a copy of your personal data in electronic format and/or request that we transmit your personal data to another person or entity. You can request that we transmit your data when we are processing your personal data on the 1) legal basis of consent or 2) for the performance of contract.</td>
                                </tr>
                                <tr>
                                    <td><strong>Not be subject to automated decision making</strong></td>
                                    <td>Not be subject to a decision based solely on automated decision making (decisions without human involvement), including profiling, where the decision would have a legal effect on you or produce a similarly significant effect. Note that we do not currently carry out this type of automated decision making in Passport.</td>
                                </tr>
                                <tr>
                                    <td><strong>Withdrawal of consent</strong></td>
                                    <td>Withdraw your consent for us to collect, use or otherwise process your personal data. You can do this if Passport is processing your personal data solely on the legal basis of consent. Your withdrawal of consent will not be retroactive and will only take effect at the time withdrawal is made.</td>
                                </tr>
                                <tr>
                                    <td><strong>Right to lodge a complaint</strong></td>
                                    <td>If you believe we have not complied with applicable data protection laws, you may contact your local data protection authority about any questions or concerns.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <h3>II. Exercising Your Rights</h3>
                    <p>
                        You can request to exercise your rights by contacting us through the details provided in Section 10 of this Policy.
                    </p>
                    
                    <p>
                        We may need to request specific information from you to help us confirm your identity and facilitate your request. We may decline requests to exercise these rights where we are unable to authenticate you as the person to whom the data relates. We will not discriminate against you for exercising any of your rights.
                    </p>
                    
                    <p>
                        You may designate, in writing or through a power of attorney, an authorized agent to make requests on your behalf to exercise your rights. Before accepting such a request from an agent, we will require the agent to provide proof you have authorized it to act on your behalf, and we may need you to verify your identity directly with us.
                    </p>
                    
                    <h2>3. Personal Data We Collect About You</h2>
                    <p>
                        These tables set out the categories of personal data we collect and use.
                    </p>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th colSpan="2">Collected when you sign up for our Passport Service or when you update your account</th>
                                </tr>
                                <tr>
                                    <th>Categories</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>User Data</strong></td>
                                    <td>
                                        This is the personal data that we need to create your Passport account and that enables you to use the Passport Service.
                                        <ul>
                                            <li>Username</li>
                                            <li>First and last name (legal name)</li>
                                            <li>Email address</li>
                                            <li>Age</li>
                                        </ul>
                                        We receive this information from you directly when you sign up for the Passport Service, except for the username which is generated automatically after submitting the sign-up form. You also have the option to provide us with additional data by creating a user profile. This user profile may include the following personal data:
                                        <ul>
                                            <li>First and last name (legal name)</li>
                                            <li>Headshot (Photo)</li>
                                            <li>Pen Name</li>
                                            <li>Mailing Address</li>
                                            <li>Billing Address</li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th colSpan="2">Collected through your usage of the Passport Service and this website</th>
                                </tr>
                                <tr>
                                    <th>Categories</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Usage Data</strong></td>
                                    <td>
                                        This is personal data collected and processed about Publishers when accessing or using the Passport Service.
                                        <br /><br />
                                        <strong>Information about how you use our Passport Service:</strong>
                                        <br />
                                        Examples include:
                                        <ul>
                                            <li>Your actions with the Passport Service, such as the time spent on each page within an application; the details about the path followed within an application; and the sequence of pages visited.</li>
                                            <li>Content you post to any part of the Passport Service. For example, images, audio, text, titles, descriptions, communications, and other types of content, including manuscripts, narrations, reviews and ratings.</li>
                                        </ul>
                                        <strong>Your Technical Data:</strong>
                                        <br />
                                        Examples include:
                                        <ul>
                                            <li>IP addresses</li>
                                            <li>URL information</li>
                                            <li>Information about the devices you use, such as:
                                                <ul>
                                                    <li>Device IDs</li>
                                                    <li>Browser type</li>
                                                    <li>Operating system</li>
                                                    <li>Network and device performance</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th colSpan="2">Additional information you may choose to give us</th>
                                </tr>
                                <tr>
                                    <th>Categories</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Survey and Research Data</strong></td>
                                    <td>When you complete any forms, respond to a survey, or take part in user research, we collect and use the personal data you provide.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <h2>4. Purpose of Processing</h2>
                    <p>
                        The table below sets out:
                    </p>
                    <ul>
                        <li>our purpose for processing your personal data</li>
                        <li>our legal justifications (each called a 'legal basis') under applicable data privacy laws, for each purpose</li>
                        <li>categories of personal data, which we use for each purpose. See more about these categories in Section 3.</li>
                    </ul>
                    
                    <p>
                        Here is a general explanation of each 'legal basis' utilized by Passport to help you understand the table:
                    </p>
                    
                    <ul>
                        <li><strong>Performance of a Contract</strong>: When it's necessary for Passport, or a third party, to process your personal data to fulfill its responsibilities under a legal contract with you, or if necessary in order to take steps at your request prior to entering into a contract. This includes Passport's obligations under the Audiobook License Agreement and the Passport Terms of Use to provide the Passport Service to you.</li>
                        <li><strong>Legitimate Interest</strong>: When Passport or a third-party has a legitimate interest in using your personal data, which is necessary and justified. This is commonly used as a legal basis for fraud prevention, network and information security and to protect public security. It may also be used to provide services which improve the Passport Service for all users.</li>
                        <li><strong>Consent</strong>: When you provide your consent to Passport's use of or transfer of your personal data for a certain purpose.</li>
                        <li><strong>Compliance with Legal Obligations</strong>: When Passport must process your personal data to comply with a law.</li>
                    </ul>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th>Purpose for processing your Data</th>
                                    <th>Legal basis that permits the purpose</th>
                                    <th>Categories of personal data used for the purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>To provide the Passport Service (as defined in Section 1)</td>
                                    <td>Performance of a Contract<br />Legitimate Interest<br />Consent</td>
                                    <td>User Data<br />Usage Data</td>
                                </tr>
                                <tr>
                                    <td>To understand, diagnose, troubleshoot, and fix issues with the Passport Service.</td>
                                    <td>Performance of a Contract</td>
                                    <td>User Data<br />Usage Data</td>
                                </tr>
                                <tr>
                                    <td>To evaluate and develop new features, technologies, and improvements to the Passport Service.</td>
                                    <td>Legitimate Interest</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                </tr>
                                <tr>
                                    <td>For marketing, promotion, and advertising purposes.</td>
                                    <td>Consent<br />Legitimate Interest</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                </tr>
                                <tr>
                                    <td>To comply with legal obligations and law enforcement requests.</td>
                                    <td>Compliance with legal obligations<br />Legitimate Interest</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                </tr>
                                <tr>
                                    <td>To fulfill contractual obligations with third parties.</td>
                                    <td>Legitimate interest</td>
                                    <td>User Data<br />Usage Data</td>
                                </tr>
                                <tr>
                                    <td>To establish, exercise, or defend legal claims.</td>
                                    <td>Compliance with legal obligations<br />Legitimate interest</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                </tr>
                                <tr>
                                    <td>To conduct business planning, reporting, and forecasting.</td>
                                    <td>Legitimate interest</td>
                                    <td>User Data<br />Usage Data</td>
                                </tr>
                                <tr>
                                    <td>To process your payment or payout.</td>
                                    <td>Performance of a Contract<br />Compliance with legal obligations</td>
                                    <td>User Data</td>
                                </tr>
                                <tr>
                                    <td>To detect and prevent fraud. For example, fraudulent payments and fraudulent use of the Passport Service.</td>
                                    <td>Legitimate interest</td>
                                    <td>User Data<br />Usage Data</td>
                                </tr>
                                <tr>
                                    <td>To conduct research and surveys</td>
                                    <td>Legitimate interest</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <h2>5. Sharing of Information</h2>
                    <p>
                        This section sets out the categories of recipients of the personal data collected or generated through your use of the Passport Service. The table below provides details of who we may share your personal data with, and why:
                    </p>
                    
                    <div className="table-container">
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th>Categories of Recipients</th>
                                    <th>Categories of Data You Can Choose to Share</th>
                                    <th>Reason for Sharing</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Other INaudio group companies</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                    <td>To carry out our daily business operations and to maintain and provide the Passport Service to you.</td>
                                </tr>
                                <tr>
                                    <td>Law enforcement and other authorities</td>
                                    <td>User Data<br />Usage Data</td>
                                    <td>When we, in good faith, believe that it is necessary: to comply with a legal obligation to respond to a valid legal process (such as a search warrant, court order, or subpoena) for our own or a third party's justifiable interest, relating to: national security law enforcement litigation (a court case) criminal investigation protecting someone's safety preventing death or imminent bodily harm</td>
                                </tr>
                                <tr>
                                    <td>Purchasers of our business</td>
                                    <td>User Data<br />Usage Data<br />Survey and Research Data</td>
                                    <td>If we were to sell or negotiate to sell our business to a buyer or possible buyer. In this case, we may transfer your personal data to a successor or affiliate as part of that transaction.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <h2>6. Data Retention</h2>
                    <p>
                        We keep your personal data only as long as necessary to provide you with the Passport Service and for Passport legitimate and essential business purposes, such as:
                    </p>
                    
                    <ul>
                        <li>maintaining the performance of the Passport Service</li>
                        <li>making data-driven business decisions about new features and offerings</li>
                        <li>complying with our legal obligations; and</li>
                        <li>resolving disputes</li>
                    </ul>
                    
                    <p>
                        When determining the retention period, we take into account various criteria, such as the type of information, the nature and length of our relationship with you, the impact on that relationship if data is deleted, and mandatory retention periods provided by or permitted by applicable law.
                    </p>
                    
                    <h2>7. Transfer to or from Other Countries</h2>
                    <p>
                        Due to the global nature of our business, Passport shares personal data internationally with its affiliates, subcontractors and partners when carrying out the activities described in this Policy and in the Terms of Use. They may process your data in countries whose data protection laws are not considered to be as strong as US laws or the laws which apply where you live. For example, they may not give you the same rights over your data.
                    </p>
                    
                    <p>
                        Whenever we transfer personal data internationally, we use reasonable efforts and tools to:
                    </p>
                    
                    <ul>
                        <li>make sure the data transfer complies with applicable law; and</li>
                        <li>help to give your data the same level of protection as it has in the US and the laws which apply where you live.</li>
                    </ul>
                    
                    <h2>8. Keeping Your Personal Data Safe</h2>
                    <p>
                        We're committed to protecting our users' personal data. We put in place appropriate technical and organizational measures to help protect the security of your personal data. However, be aware that no system is ever completely secure.
                    </p>
                    
                    <p>
                        We have implemented various safeguards to guard against unauthorized access and unnecessary retention of personal data in our systems. These include pseudonymisation, encryption, access, and retention policies.
                    </p>
                    
                    <p>
                        To protect your user account, we strongly encourage you to:
                    </p>
                    
                    <ul>
                        <li>use a strong password which you only use for your Passport account;</li>
                        <li>never share your password with anyone;</li>
                        <li>limit access to your computer and browser; and</li>
                        <li>log out once you have finished using the Passport Service on a shared device</li>
                    </ul>
                    
                    <p>
                        If other individuals have access to your Passport account (for example if you've given them permission to use your account on a shared device), then they can access personal data, controls and the Passport Service available in your account.
                    </p>
                    
                    <h2>9. Changes to this Policy</h2>
                    <p>
                        We may occasionally make changes to this Policy.
                    </p>
                    
                    <p>
                        When we make material changes to this Policy, we will post them here and update the "Last Updated" data at the top of this document. If we make material changes to how we treat your personal data, we will provide you with a prominent notice of such changes as appropriate under the circumstances. For example, we may display a prominent notice within the Passport Service or send you an email. Continued use of the Passport Services after any changes is deemed to be an acknowledgement and acceptance of these changes.
                    </p>
                    
                    <h2>10. Contact Us</h2>
                    <p>
                        If you have any questions about this Policy, please contact us by emailing <a href="mailto:pub-support@inaudio.com">pub-support@inaudio.com</a>.
                    </p>
                    
                    <p>
                        We hope you enjoy Passport!
                    </p>
                </div>
            </div>
        </>
    );
};

export default PrivacyPolicy; 